import { getUserPermissions } from './permissionsHelper';

/**
 * All available menu items for UI selection
 */
export const ALL_MENU_ITEMS = [
  { key: 'inboxView', label: 'INBOX_VIEW' },
  { key: 'conversations', label: 'CONVERSATIONS' },
  { key: 'captain', label: 'CAPTAIN' },
  { key: 'contacts', label: 'CONTACTS' },
  { key: 'reports', label: 'REPORTS' },
  { key: 'campaigns', label: 'CAMPAIGNS' },
  { key: 'settings', label: 'SETTINGS' },
  { key: 'knowledge', label: 'KNOWLEDGE' },
  { key: 'google_sheet', label: 'GOOGLE_SHEET' },
  { key: 'knowledge_improvement', label: 'KNOWLEDGE_IMPROVEMENT' },
  { key: 'appointments', label: 'APPOINTMENTS' },
  { key: 'branches', label: 'BRANCHES' },
  { key: 'rental_branches', label: 'RENTAL_BRANCHES' },
  { key: 'rental_bookings', label: 'RENTAL_BOOKINGS' },
  { key: 'whitelist', label: 'WHITELIST' },
  { key: 'condo_management', label: 'CONDO_MANAGEMENT' },
  { key: 'eVoucher', label: 'E_VOUCHER' },
];

// Menu items that require administrator role
export const ADMIN_ONLY_MENU_KEYS = [
  'campaigns',
  'google_sheet',
  'whitelist',
  'condo_management',
  'eVoucher',
];

// Map menu keys to their required feature flags
// Note: Only map features that should be HIDDEN when disabled
// Core features (conversations, settings, contacts, reports, campaigns) are always available
export const MENU_TO_FEATURE_MAP = {
  // Core features - no feature flag requirement (always available)
  // inboxView: removed - core feature
  // captain: removed - core feature
  // contacts: removed - core feature
  // reports: removed - core feature
  // campaigns: removed - core feature

  // Optional features - require feature flags
  knowledge: 'knowledge',
  google_sheet: 'google_sheet',
  knowledge_improvement: 'knowledge_improvement',
  appointments: 'appointments',
  branches: 'branches',
  rental_branches: 'rental_branches',
  rental_bookings: 'rental_bookings',
  whitelist: 'whitelist',
  condo_management: 'condo_management',
  eVoucher: 'eVoucher',
};

/**
 * Get allowed menu items for current user
 * Priority: Agent > Team > All (default)
 *
 * @param {Object} currentUser - Current user object
 * @param {Number} accountId - Current account ID
 * @param {Array} teams - All teams in the account
 * @returns {Array|null} - Array of allowed menu keys, or null for "no restrictions"
 */
export const getAllowedMenuItems = (currentUser, accountId, teams = []) => {
  const currentAccount = currentUser.accounts?.find(
    acc => Number(acc.id) === Number(accountId)
  );

  if (!currentAccount) return null;

  // 1. Check agent-level permissions (highest priority)
  const agentMenuItems = currentAccount.allowed_menu_items || [];
  if (agentMenuItems.length > 0) {
    return agentMenuItems;
  }

  // 2. Check team-level permissions (merge all teams user belongs to)
  const userTeams = teams.filter(team => team.is_member);
  const teamMenuItems = new Set();

  userTeams.forEach(team => {
    const teamMenus = team.allowed_menu_items || [];
    teamMenus.forEach(menu => teamMenuItems.add(menu));
  });

  if (teamMenuItems.size > 0) {
    return Array.from(teamMenuItems);
  }

  // 3. Default: no restrictions (return null)
  return null;
};

/**
 * Check if user can access a specific menu item
 *
 * @param {String} menuKey - Menu item key (e.g., 'conversations')
 * @param {Object} currentUser - Current user object
 * @param {Number} accountId - Current account ID
 * @param {Array} teams - All teams in the account
 * @returns {Boolean} - True if user can access this menu
 */
export const canAccessMenuItem = (menuKey, currentUser, accountId, teams) => {
  const allowedItems = getAllowedMenuItems(currentUser, accountId, teams);

  // null = no restrictions, show all
  if (allowedItems === null) return true;

  // Check if menu key is in allowed list
  return allowedItems.includes(menuKey);
};
